/**
 * SpectatorMovementComponent - physics-free 6-DOF fly-through movement.
 * WASD moves relative to the camera's facing direction (including vertical when looking up/down).
 * Mouse wheel adjusts movement speed persistently. No gravity, no collision.
 */
import * as THREE from 'three';

import { CHARACTER_MAX_SPEED } from '../../const.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { MathHelpers } from '../../utils/MathHelpers.js';
import { property } from '../../utils/serialization/decorator.js';

import { BasePawnMovementComponent } from './BasePawnMovementComponent.js';

import type { SceneComponentOptions } from '../SceneComponent.js';

export interface SpectatorMovementOptions extends SceneComponentOptions {
  /** Base movement speed in units per second */
  maxSpeed?: number;
  /** Minimum speed when using the mouse wheel to decrease speed */
  minSpeed?: number;
  /** Maximum speed when using the mouse wheel to increase speed */
  maxSpeedCap?: number;
  /** Multiplicative factor applied per scroll tick (e.g. 1.2 = 20% faster per tick) */
  scrollSpeedFactor?: number;
  /** Damp lambda for acceleration */
  accelerationLambda?: number;
  /** Damp lambda for deceleration */
  decelerationLambda?: number;
  /** Look sensitivity multiplier for mouse input */
  lookSpeed?: number;
  /** Look sensitivity multiplier for speed-based (gamepad) input */
  lookSpeedContinuous?: number;
  /** Minimum pitch angle in radians */
  minPitchAngle?: number;
  /** Maximum pitch angle in radians */
  maxPitchAngle?: number;
}

@EngineClass('SpectatorMovementComponent')
export class SpectatorMovementComponent extends BasePawnMovementComponent {

  @property()
  public maxSpeed: number = CHARACTER_MAX_SPEED * 4;

  @property({ min: 0.1 })
  public minSpeed: number = 1;

  @property({ min: 1 })
  public maxSpeedCap: number = 1000;

  @property({ min: 1.01, step: 0.05, decimals: 2 })
  public scrollSpeedFactor: number = 1.2;

  @property()
  public accelerationLambda: number = 20;

  @property()
  public decelerationLambda: number = 10;

  @property()
  public lookSpeed: number = 0.05;

  @property()
  public lookSpeedContinuous: number = 0.05;

  @property({ type: 'number' })
  public minPitchAngle: number = -Math.PI / 2;

  @property({ type: 'number' })
  public maxPitchAngle: number = Math.PI / 2;

  private forwardVelocity: number = 0;
  private rightVelocity: number = 0;
  private upVelocity: number = 0;

  private readonly pitchForwardScratch: THREE.Vector3 = new THREE.Vector3();

  public override initialize(options?: SpectatorMovementOptions): void {
    super.initialize(options);
    this.maxSpeed = options?.maxSpeed ?? this.maxSpeed;
    this.minSpeed = options?.minSpeed ?? this.minSpeed;
    this.maxSpeedCap = options?.maxSpeedCap ?? this.maxSpeedCap;
    this.scrollSpeedFactor = options?.scrollSpeedFactor ?? this.scrollSpeedFactor;
    this.accelerationLambda = options?.accelerationLambda ?? this.accelerationLambda;
    this.decelerationLambda = options?.decelerationLambda ?? this.decelerationLambda;
    this.lookSpeed = options?.lookSpeed ?? this.lookSpeed;
    this.lookSpeedContinuous = options?.lookSpeedContinuous ?? this.lookSpeedContinuous;
    this.minPitchAngle = options?.minPitchAngle ?? this.minPitchAngle;
    this.maxPitchAngle = options?.maxPitchAngle ?? this.maxPitchAngle;
  }

  /**
   * Handles mouse wheel events to adjust movement speed.
   * Adjusts the current speed multiplicatively so that slow speeds scale finely
   * and fast speeds scale coarsely (exponential feel).
   * @param e - The wheel event
   * @returns True if the event was handled
   */
  public override handleMouseWheel(e: WheelEvent): boolean {
    // Normalize delta: positive = scroll up (speed up), negative = scroll down (slow down)
    const delta = -e.deltaY;
    if (delta > 0) {
      this.maxSpeed = Math.min(this.maxSpeed * this.scrollSpeedFactor, this.maxSpeedCap);
    } else if (delta < 0) {
      this.maxSpeed = Math.max(this.maxSpeed / this.scrollSpeedFactor, this.minSpeed);
    }
    return true;
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);

    const owner = this.getActor()!;
    if (owner.isSimulatedProxy()) return;

    const rootComponent = owner.rootComponent;
    const effectiveSpeed = this.maxSpeed * this.speedModifier;

    // Accelerate/decelerate along each axis
    this.forwardVelocity = this.updateVelocity(this.forwardInput.value, this.forwardVelocity, effectiveSpeed, deltaTime);
    this.rightVelocity = this.updateVelocity(this.rightInput.value, this.rightVelocity, effectiveSpeed, deltaTime);
    // zoomInput maps to vertical (up/down) movement
    this.upVelocity = this.updateVelocity(this.zoomInput.value, this.upVelocity, effectiveSpeed, deltaTime);

    // Move in camera-relative space: forward follows the look direction (full 6-DOF)
    const forward = owner.forwardDirection().multiplyScalar(this.forwardVelocity * deltaTime);
    const right = owner.rightDirection().multiplyScalar(this.rightVelocity * deltaTime);
    const up = new THREE.Vector3(0, 1, 0).multiplyScalar(this.upVelocity * deltaTime);

    rootComponent.addWorldPosition(forward.add(right).add(up));

    // Yaw (left/right look) — rotate the whole pawn
    if (this.lookRightInput.value !== 0) {
      const yawAmount = this.lookRightInput.isAbsolute
        ? this.lookRightInput.value * this.lookSpeed
        : this.lookRightInput.value * this.lookSpeedContinuous * deltaTime;
      rootComponent.addWorldRotation(MathHelpers.makeRotation({ yaw: yawAmount }));
    }

    // Pitch (up/down look) — clamp to avoid gimbal flipping
    if (this.lookUpInput.value !== 0) {
      const currentPitch = this.extractPitchFromForward(rootComponent.quaternion);
      const pitchDelta = this.lookUpInput.isAbsolute
        ? this.lookUpInput.value * this.lookSpeed
        : this.lookUpInput.value * this.lookSpeedContinuous * deltaTime;
      const newPitch = THREE.MathUtils.clamp(
        currentPitch + pitchDelta,
        this.minPitchAngle,
        this.maxPitchAngle
      );
      rootComponent.addLocalRotation(MathHelpers.makeRotation({ pitch: newPitch - currentPitch }));
    }
  }

  private extractPitchFromForward(quaternion: THREE.Quaternion): number {
    const forwardY = this.pitchForwardScratch.set(0, 0, -1).applyQuaternion(quaternion).y;
    return Math.asin(THREE.MathUtils.clamp(forwardY, -1, 1));
  }

  private updateVelocity(input: number, current: number, maxSpeed: number, deltaTime: number): number {
    if (input !== 0) {
      const target = THREE.MathUtils.clamp(input * maxSpeed, -maxSpeed, maxSpeed);
      return THREE.MathUtils.damp(current, target, this.accelerationLambda, deltaTime);
    }
    const next = THREE.MathUtils.damp(current, 0, this.decelerationLambda, deltaTime);
    return Math.abs(next) < 0.01 ? 0 : next;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Spectator';
  }
}
